import type { CSSProperties } from "react";

import type { ProgramCard } from "../lib/api";
import { spiralOrder } from "../lib/dotPaths";
import { plural } from "../lib/format";
import { identicon } from "../lib/identicon";
import { haptic } from "../lib/telegram";

/** Откуда программа: взятая из таблицы правится иначе, чем собранная руками. */
const SOURCE: Record<string, string> = {
  manual: "вручную",
  import_xlsx: "из таблицы",
  import_sheets: "из Google Sheets",
  generator: "генератор",
  template: "готовая",
};

const ORDER = spiralOrder(5);

/** Больше сегментов в строку не помещается; остаток — числом. */
const MAX_BLOCKS = 10;

interface Props {
  program: ProgramCard;
  /** Место в списке: карточки появляются по очереди. */
  index: number;
  /** Строка под названием, если экрану нужна своя. */
  meta?: string;
  onOpen: () => void;
}

/**
 * Карточка программы — одна на список программ и на выбор тренировки.
 *
 * Слева знак из точек: у каждой программы свой, он проявляется по спирали при
 * появлении и вспыхивает под пальцем. Блоки показаны сегментами — структуру
 * цикла видно, не читая цифр.
 */
export default function ProgramTile({ program: p, index, meta, onOpen }: Props) {
  const cells = identicon(`${p.id}:${p.name}`);
  const blocks = Math.min(p.block_count, MAX_BLOCKS);

  return (
    <button
      className="pcard"
      style={{ "--k": index } as CSSProperties}
      onClick={() => {
        haptic();
        onOpen();
      }}
    >
      <span className="pcard__emblem" aria-hidden="true">
        <span className="pcard__grid">
          {cells.map((on, i) => (
            <span
              key={i}
              className={`pcard__dot ${on ? "pcard__dot--on" : ""}`}
              style={{ "--n": ORDER[i] / 24 } as CSSProperties}
            />
          ))}
        </span>
      </span>

      <span className="pcard__body">
        <span className="pcard__name">{p.name}</span>
        <span className="pcard__meta">
          {meta ??
            `${plural(p.day_count, "тренировка", "тренировки", "тренировок")} · ${plural(
              p.exercise_count,
              "упражнение",
              "упражнения",
              "упражнений",
            )}`}
        </span>
        <span className="pcard__foot">
          {p.block_count > 0 && (
            <span className="pcard__blocks">
              {Array.from({ length: blocks }, (_, i) => (
                <span key={i} className="pcard__block" />
              ))}
              <span className="pcard__blocks-key">
                {plural(p.block_count, "блок", "блока", "блоков")}
              </span>
            </span>
          )}
          {SOURCE[p.source] && <span className="pcard__source">{SOURCE[p.source]}</span>}
          {/* Чужая программа в своём списке должна быть опознаваема сразу:
              её правит тренер, и правки приходят без спроса. */}
          {p.assigned_by && (
            <span className="pcard__source">от тренера · {p.assigned_by}</span>
          )}
          {/* Правка тренера приходит без спроса — молча подменять план нечестно. */}
          {p.coach_updated && <span className="pcard__source">обновлено</span>}
        </span>
      </span>
    </button>
  );
}
